import { useAuth } from '@/context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Zap, Settings, Shield, LogOut, User, Menu } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/upload': 'Upload Video',
  '/presets': 'Preset Library',
  '/library': 'Library',
  '/pipeline': 'Pipeline',
  '/pricing': 'Plans & Credits',
  '/settings': 'Settings',
  '/admin': 'Operator Console',
};

export const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  let title = pageTitles[location.pathname] || '';
  if (location.pathname.startsWith('/video/')) title = 'Video Details';
  if (location.pathname.startsWith('/generate/')) title = 'Generate Content';
  if (location.pathname === '/presets/builder') title = 'AI Preset Builder';

  const totalCredits = (user?.monthly_credits || 0) + (user?.purchased_credits || 0);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const avatarSrc = user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-border/50 bg-zinc-950/60 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 md:px-8" data-testid="header">
      <div className="flex items-center gap-3 min-w-0">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 -ml-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Open navigation menu"
            data-testid="header-menu-button"
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
          </button>
        )}
        <h2 className="font-heading text-base sm:text-lg md:text-xl font-semibold tracking-tight text-foreground truncate">{title}</h2>
      </div>
      <div className="flex items-center gap-3 sm:gap-4">
        {/* Credit balance */}
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-secondary/40 border border-white/5 hover:border-primary/20 transition-all text-sm cursor-pointer"
          data-testid="header-credits"
        >
          <Zap className="w-3.5 h-3.5 text-primary" />
          <span className="font-medium">{totalCredits}</span>
          <span className="text-muted-foreground text-xs hidden sm:inline">credits</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50" data-testid="header-profile-trigger" aria-label="Open profile menu">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="w-9 h-9 rounded-full object-cover border border-border hover:border-primary/30 transition-colors"
                  data-testid="header-avatar-image"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-heading text-sm font-bold border border-border hover:border-primary/30 transition-colors" data-testid="header-avatar-placeholder">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-zinc-950 border-white/10" data-testid="header-profile-menu">
            <DropdownMenuLabel className="pb-1">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground font-normal truncate">{user?.email}</p>
              {isAdmin && (
                <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] uppercase tracking-wider text-amber-400">
                  <Shield className="w-3 h-3" />
                  {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="header-menu-settings">
              <User className="w-4 h-4 mr-2" />
              Profile & Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/pricing')} data-testid="header-menu-billing">
              <Zap className="w-4 h-4 mr-2" />
              Plans & Credits
            </DropdownMenuItem>
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/admin')}
                  className="text-amber-400 focus:text-amber-300 focus:bg-amber-500/10"
                  data-testid="header-menu-admin"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Operator Console
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive focus:bg-destructive/10"
              data-testid="header-menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
